import * as React from 'react';
import * as Yup from 'yup';
import { Formik, ErrorMessage } from 'formik';
import { VStack, Heading, ScrollView, Text } from 'native-base';

import { PrimaryButton, TextField } from '../../';
import { InputType, Status } from '@breakroom-nx/helpers';
import { useAppDispatch, openModal, flipToPane } from '@breakroom-nx/redux';
import { auth } from '@breakroom-nx/apis';
import { sendPasswordResetEmail } from 'firebase/auth';

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Enter the email associated with your account'),
});

interface ForgotPasswordFormValues {
  email: string;
}

export function ForgotPassword() {
  const initialValues: ForgotPasswordFormValues = { email: '' };
  const dispatch = useAppDispatch();

  return (
    <ScrollView
      flex={1}
      maxHeight={'40vh'}
      showsVerticalScrollIndicator={true}
      _contentContainerStyle={{
        pb: 8,
      }}
    >
      <VStack space={7} alignItems={'flex-start'}>
        <Heading mt={7} fontSize={30} lineHeight={34}>
          Forgot password?
        </Heading>
        <Formik
          initialValues={initialValues}
          validationSchema={ForgotPasswordSchema}
          onSubmit={(values, { setSubmitting }) => {
            sendPasswordResetEmail(auth, values.email);
            setSubmitting(false);
            dispatch(
              openModal({
                modalType: 'Alert',
                modalProps: {
                  status: Status.success,
                  title:
                    'If this email is associated with an account, you will receive a reset email in your inbox within a few minutes.',
                },
              })
            );
            dispatch(flipToPane('Login'));
          }}
        >
          {({
            values,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <>
              <VStack space={1.5} width={'full'}>
                <TextField
                  disabled={isSubmitting}
                  type={InputType.Email}
                  onChangeText={handleChange('email')}
                  onBlur={handleBlur('email')}
                  value={values.email}
                  autoCompleteType={'email'}
                  autoFocus
                />
                <Text variant={'error'}>
                  <ErrorMessage name="email" />
                </Text>
              </VStack>
              <VStack space={3} width={'full'} mt={7}>
                <PrimaryButton
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  text={'Reset Password'}
                  onPress={handleSubmit}
                />
              </VStack>
            </>
          )}
        </Formik>
      </VStack>
    </ScrollView>
  );
}

export default React.memo(ForgotPassword);
